import { Link } from "react-router-dom";
import { ShieldCheck, CloudCheck, HardDrive, RotateCcw, MessageSquareText } from "lucide-react";
import { AUTHORITY_ITEM_COUNT } from "../../data/authorityChecklist";
import { AuthorityWatchRow } from "./AuthorityWatchRow";

const DAY = 86400000;
const fmt = (iso) => new Date(iso + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });

export function AuthorityStatusCard({ state, update, reset, user, synced, reminders, watch, checkNow, checking }) {
  const done = Object.keys(state.checked || {}).length;
  const pct = Math.round((done / AUTHORITY_ITEM_COUNT) * 100);
  const mc = (state.mc_number || "").replace(/\D/g, "");
  const dot = (state.dot_number || "").replace(/\D/g, "");
  const checkQ = mc ? `MC-${mc}` : dot;

  return (
    <div className="rounded-2xl border border-white/10 bg-neutral-900/70 backdrop-blur p-5 sm:p-6" data-testid="authority-status-card">
      <div className="flex items-center justify-between gap-4 mb-3">
        <div className="text-[11px] uppercase tracking-widest text-white/50 font-bold">Your progress</div>
        <div className="text-sm font-black tabular-nums" data-testid="authority-progress">
          {done}<span className="text-white/40">/{AUTHORITY_ITEM_COUNT}</span> <span className="text-[#FF5A1F]">· {pct}%</span>
        </div>
      </div>
      <div className="h-2.5 rounded-full bg-white/10 overflow-hidden mb-6">
        <div className="h-full rounded-full bg-[#FF5A1F] transition-[width] duration-500" style={{ width: `${pct}%` }} />
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <Field label="MC applied on" type="date" value={state.mc_applied_at} onChange={(v) => update({ mc_applied_at: v })} testid="authority-applied-input" />
        <Field label="MC #" placeholder="e.g. 1234567" value={state.mc_number} onChange={(v) => update({ mc_number: v })} testid="authority-mc-input" />
        <Field label="USDOT #" placeholder="e.g. 3738067" value={state.dot_number} onChange={(v) => update({ dot_number: v })} testid="authority-dot-input" />
      </div>

      <AppliedHint appliedAt={state.mc_applied_at} hasNumber={!!(mc || dot)} />
      <AuthorityWatchRow user={user} hasNumber={(mc || dot).length >= 6} watch={watch} checkNow={checkNow} checking={checking} />
      <RemindersRow appliedAt={state.mc_applied_at} user={user} reminders={reminders} />

      <div className="flex flex-wrap items-center gap-3 mt-5">
        {checkQ ? (
          <Link to={`/broker-check?q=${encodeURIComponent(checkQ)}`} className="inline-flex items-center gap-2 bg-[#FF5A1F] hover:bg-[#FF7038] text-black font-bold rounded-full px-5 py-2.5 text-sm transition-colors" data-testid="authority-brokercheck-link">
            <ShieldCheck className="w-4 h-4" /> See what brokers see for {checkQ}
          </Link>
        ) : (
          <span className="text-xs text-white/40">Add your MC# or DOT# to run it through BrokerCheck the moment it posts.</span>
        )}
        <div className="ml-auto flex items-center gap-3 text-xs text-white/45">
          <span className="inline-flex items-center gap-1" data-testid="authority-sync-state">
            {user ? <><CloudCheck className="w-3.5 h-3.5 text-emerald-400" />{synced ? "Synced to your account" : "Saving…"}</> : <><HardDrive className="w-3.5 h-3.5" />Saved on this device</>}
          </span>
          {done > 0 && (
            <button type="button" onClick={() => window.confirm("Clear all checked items?") && reset()} className="inline-flex items-center gap-1 hover:text-white transition-colors" data-testid="authority-reset-btn">
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testid, ...rest }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-widest text-white/45 font-bold mb-1.5">{label}</span>
      <input
        {...rest}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-lg bg-black/40 border border-white/10 focus:border-[#FF5A1F] outline-none px-3 py-2.5 text-sm text-white placeholder:text-white/25 transition-colors [color-scheme:dark]"
        data-testid={testid}
      />
    </label>
  );
}

function RemindersRow({ appliedAt, user, reminders }) {
  if (!appliedAt) return null;
  const base = "flex flex-wrap items-start gap-2 text-sm mt-3 rounded-xl border px-3.5 py-3";
  if (!user) {
    return (
      <div className={`${base} border-white/10 bg-white/[0.03] text-white/60`} data-testid="authority-reminders-anon">
        <MessageSquareText className="w-4 h-4 mt-0.5 text-[#FF5A1F] shrink-0" />
        <span>Want a text on day 14 (number should be posted) and day 21 (should flip ACTIVE)? <Link to="/signup" className="text-[#FF5A1F] font-bold hover:underline">Create a free account</Link> and turn on texts.</span>
      </div>
    );
  }
  if (!reminders?.day14_on) return null;
  const { day14_on, day21_on, day14_sent_at, day21_sent_at, day21_basis, channel } = reminders;
  const via = channel === "sms" ? "Texting you" : channel === "email" ? "Emailing you" : "Reminders paused —";
  const when21 = day21_basis === "mc_posted" ? "21 days after your MC# posted" : "day 35 if no MC# is entered";
  return (
    <div className={`${base} border-[#FF5A1F]/25 bg-[#FF5A1F]/[0.06] text-white/75`} data-testid="authority-reminders">
      <MessageSquareText className="w-4 h-4 mt-0.5 text-[#FF5A1F] shrink-0" />
      <span className="flex-1 min-w-0">
        {channel === "none" ? (
          <>Reminders paused — add your email or <Link to="/app/account/settings" className="text-[#FF5A1F] font-bold hover:underline">turn on texts</Link> to get them.</>
        ) : (
          <>
            {via} <b className="text-white">{fmt(day14_on)}</b> (MC# should be posted){day14_sent_at && <span className="text-emerald-400"> ✓ sent</span>}
            {" "}and <b className="text-white">{fmt(day21_on)}</b> (should flip ACTIVE — {when21}){day21_sent_at && <span className="text-emerald-400"> ✓ sent</span>}.
            {channel === "email" && <> <Link to="/app/account/settings" className="text-[#FF5A1F] font-bold hover:underline" data-testid="authority-reminders-enable-sms">Turn on texts</Link> to get these by SMS.</>}
          </>
        )}
      </span>
    </div>
  );
}

function AppliedHint({ appliedAt, hasNumber }) {
  if (!appliedAt || hasNumber) return null;
  const applied = new Date(appliedAt + "T12:00:00");
  if (Number.isNaN(applied.getTime())) return null;
  const days = Math.floor((Date.now() - applied.getTime()) / DAY);
  const eta = new Date(applied.getTime() + 14 * DAY).toLocaleDateString("en-US", { month: "short", day: "numeric" });
  return (
    <p className="text-sm text-white/60 mt-4" data-testid="authority-applied-hint">
      Applied <b className="text-white">{days} day{days === 1 ? "" : "s"}</b> ago — FMCSA's new system usually posts the number in ~14 days, so expect it around <b className="text-white">{eta}</b>. Then the 21-day clock to ACTIVE starts.
    </p>
  );
}
